import {getStronglyTypedKeys} from '../../utils/getStronglyTypedKeys';
import {
  JS_TO_CSS_FONT_PROPERTY_MAPPINGS,
  JS_TO_STYLE_FONT_PROPERTY_MAPPINGS,
} from './constants';
import type {FontSrc, SafeFont} from './sanitizeFont';

/**
 * Referenced code from https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/468a4e2f/src/elements/inner/shared/makeFontFace.ts#L1
 * The key difference is that instead of inserting the whole CSS string as a
 * style tag text node, we return an array of strings that can be inserted
 * into a style tag's CSSStyleSheet.
 */

const constructFontSrc = (fontSrcArr: Array<FontSrc>) => {
  let resultString = '';
  fontSrcArr.forEach(({value, format, loadType}) => {
    if (loadType === 'local') {
      resultString += `local('${value}')`;
    } else {
      const fontFormat = format ? ` format('${format}')` : '';
      resultString += `url("${value}")${fontFormat}`;
    }
    resultString += ',';
  });
  return resultString.replace(/,$/, '');
};

/**
 * @param fonts An array of SafeFont objects that are obtained after the font sanitization process.
 * @returns An array of font-face rule strings.
 */
export const makeFontFace = (fonts: Array<SafeFont>): string[] => {
  const cssString: string[] = [];

  // Referenced code from https://git.corp.stripe.com/stripe-internal/stripe-js-v3/blob/a0dfc023/src/elements/inner/payment/components/NumberExfiltrationPrevention.tsx#L1

  // Render a span for every singledigit. This kicks of requests for webfonts in case
  // users are using webfonts with unicode-ranges that span only a single or a few digits.
  // This is a mitigation for a potential attack where a malicious font
  // provider would provide a CSS file with a different font for every digit
  // (using unicode-range). When a digit is entered in the card field for the
  // first time, a request to load the respective font would be made and the
  // font provider would gain knowledge of partial credit card data.

  // Also renders a span for every character in the ASCII range to preload common characters.
  // This is to prevent flash of unstyled text (FOUT) before the font is loaded.

  // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
  const preloadFontElement = document.getElementById('preload-fonts');

  fonts.forEach((fontObject) => {
    // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
    const span = document.createElement('span');
    span.textContent =
      '0123456789０１２３４５６７８９ abcdefghijklmnopqrstuvwxyz ABCDEFGHIJKLMNOPQRSTUVWXYZ !"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~¥$€£₹₽₿';

    let fontFaceString = '';
    getStronglyTypedKeys(fontObject).forEach((fontPropertyKey) => {
      let value = '';
      if (fontPropertyKey === 'src') {
        value = constructFontSrc(fontObject[fontPropertyKey]);
      } else {
        value = fontObject[fontPropertyKey] || '';
      }

      const styleAttribute =
        JS_TO_STYLE_FONT_PROPERTY_MAPPINGS[fontPropertyKey];
      if (value && styleAttribute) {
        span.style[styleAttribute] = value;
      }
      if (value && JS_TO_CSS_FONT_PROPERTY_MAPPINGS[fontPropertyKey]) {
        fontFaceString += `${JS_TO_CSS_FONT_PROPERTY_MAPPINGS[fontPropertyKey]}: ${value};`;
      }
    });
    cssString.push(`@font-face {${fontFaceString}}`);

    if (preloadFontElement) {
      preloadFontElement.appendChild(span);
    }
  });
  return cssString;
};
